import { useId, useState } from 'react'

import { CAMPAIGN_STATUS_LABELS, type CampaignStatus, type ChannelKind } from '@/api/campaigns'
import { Badge, type BadgeTone } from '@/design'

const STATUS_TONES: Record<CampaignStatus, BadgeTone> = {
  draft: 'neutral',
  scheduled: 'info',
  active: 'positive',
  paused: 'warning',
  completed: 'accent',
  archived: 'neutral',
}

export function CampaignStatusBadge({ status }: { status: CampaignStatus }) {
  return (
    <Badge tone={STATUS_TONES[status]} dot={status === 'active'}>
      {CAMPAIGN_STATUS_LABELS[status]}
    </Badge>
  )
}

/** Digital or offline, worded and toned as the Overview's channel table does. */
export function KindBadge({ kind }: { kind: ChannelKind }) {
  return <Badge tone={kind === 'offline' ? 'info' : 'neutral'}>{kind === 'offline' ? 'Offline' : 'Digital'}</Badge>
}

export function svgDataUrl(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

/** Saves an SVG document through the browser; nothing is uploaded anywhere. */
export function downloadSvg(filename: string, svg: string): void {
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }))
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.append(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

export function QrImage({ svg, label, size = 160 }: { svg: string; label: string; size?: number }) {
  return <img className="camp-qr" src={svgDataUrl(svg)} alt={label} width={size} height={size} />
}

/** A value to hand to someone else — a URL, a code — with a copy button. */
export function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false)
  const canCopy = typeof navigator !== 'undefined' && Boolean(navigator.clipboard)
  const id = useId()

  return (
    <div className="camp-copy">
      <label htmlFor={id}>{label}</label>
      <span className="camp-copy__row">
        <input id={id} readOnly value={value} onFocus={(event) => event.currentTarget.select()} />
        {canCopy ? (
          <button
            type="button"
            className="ci-button"
            aria-label={`Copy ${label.toLowerCase()}`}
            onClick={() => {
              void navigator.clipboard.writeText(value).then(() => setCopied(true))
            }}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        ) : null}
      </span>
    </div>
  )
}

export function formatDateRange(startsAt: string | null, endsAt: string | null, format: (key: string) => string): string {
  if (startsAt && endsAt) return `${format(startsAt)} – ${format(endsAt)}`
  if (startsAt) return `From ${format(startsAt)}`
  if (endsAt) return `Until ${format(endsAt)}`
  return 'No dates set'
}
